"use client"

import { useState } from "react"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { DefaultIcon } from "@modules/store/category-icons"
import { getCategoryStyle } from "@modules/store/category-style-map"

export type CatalogMenuItem = {
  id: string
  name: string
  handle: string
}

type Labels = {
  catalog: string
  allProducts: string
  catalogSoon: string
  allCategories: string
}

type Props = {
  categories: CatalogMenuItem[]
  labels: Labels
}

export default function CatalogMenuClient({ categories, labels }: Props) {
  const [open, setOpen] = useState(false)
  const isEmpty = categories.length === 0

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-brand-light to-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:from-brand-dark dark:to-brand-500"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          {open ? (
            <>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </>
          ) : (
            <>
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </>
          )}
        </svg>
        {labels.catalog}
      </button>

      {open && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          {/* Dropdown */}
          <div className="absolute left-0 top-full z-50 mt-2 w-[680px] animate-fade-up rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xl dark:border-white/10 dark:bg-[#111827]">
            {isEmpty ? (
              <LocalizedClientLink
                href="/store"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-xl border border-dashed border-brand-light/30 bg-brand-light/5 px-4 py-4 transition hover:border-brand-light/50 hover:bg-brand-light/10 dark:border-brand-dark/30 dark:bg-brand-dark/5 dark:hover:border-brand-dark/50"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-slate-400">
                  <DefaultIcon className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-sm font-semibold text-slate-900 dark:text-white">
                    {labels.allProducts}
                  </div>
                  <div className="text-xs text-slate-400">
                    {labels.catalogSoon}
                  </div>
                </div>
              </LocalizedClientLink>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {categories.map((cat, i) => {
                  const style = getCategoryStyle(cat.name)
                  return (
                    <LocalizedClientLink
                      key={cat.id}
                      href={`/categories/${cat.handle}`}
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3 rounded-xl border border-transparent px-3 py-3 transition hover:border-brand-light/20 hover:bg-brand-light/5 dark:hover:border-brand-dark/20 dark:hover:bg-brand-dark/5"
                      style={{ animationDelay: `${i * 20}ms` }}
                    >
                      <span
                        className={`flex h-9 w-9 items-center justify-center rounded-lg ${style.bg} ${style.color}`}
                      >
                        <style.Icon className="h-5 w-5" />
                      </span>
                      <div>
                        <div className="text-sm font-semibold text-slate-900 dark:text-white">
                          {cat.name}
                        </div>
                      </div>
                    </LocalizedClientLink>
                  )
                })}
              </div>
            )}
            {!isEmpty && (
              <div className="mt-3 border-t border-slate-100 pt-3 dark:border-white/5">
                <LocalizedClientLink
                  href="/store"
                  onClick={() => setOpen(false)}
                  className="text-sm font-medium text-brand-light transition hover:text-brand-600 dark:text-brand-dark dark:hover:text-brand-300"
                >
                  {labels.allCategories} →
                </LocalizedClientLink>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
